import React, { useEffect, useRef, useState } from "react"
import * as THREE from "three"
// import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader"
import { SubdivisionModifier } from "three/examples/jsm/modifiers/SubdivisionModifier"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
//Context
import { useHomeStateContext } from "../../context/homeContext"
//Components
import Locate from "./locate"
import { ContactWrapper } from "../../styles/homeStyles"
//Assets
import objiron from "./../../assets/models/head/ironclean.obj"

gsap.registerPlugin(ScrollTrigger)

// const threshold = 0.04
const multiplyOrigin = 0.035

export default function Contact({ locateText }) {
  const { toggle } = useHomeStateContext()
  const [disperse, setDisperse] = useState(true)

  const particles = useRef(null)
  // const helper = useRef(null)
  const pointer = useRef(new THREE.Vector2(-1, 1))
  const vector = useRef(new THREE.Vector3())
  const renderer = useRef(null)
  const scene = useRef(null)
  const origins = useRef(null)
  const camera = useRef(null)
  const rafContract = useRef(null)
  const rafDisperse = useRef(null)
  const scrollActive = useRef(null)
  const velocity = useRef(1)
  // const controls = useRef(null)

  const resizeWindow = _ => {
    camera.current &&
      (camera.current.aspect = window.innerWidth / window.innerHeight)
    camera.current && camera.current.updateProjectionMatrix()
    renderer.current &&
      renderer.current.setSize(window.innerWidth, window.innerHeight)
    scrollActive.current && scrollActive.current.refresh()
  }

  const onMouseMove = e => {
    const { clientX, clientY } = e
    pointer.current.x = (clientX / window.innerWidth) * 2 - 1
    pointer.current.y = -(clientY / window.innerHeight) * 2 + 1
  }

  const angleTo = (a, b) => {
    return Math.atan2(b.y - a.y, b.x - a.x)
  }

  const pointsRepulse = () => {
    particles.current.rotation.y += 0.001
    velocity.current = velocity.current - 0.015
    if (velocity.current > 0.00005) {
      let l =
        particles.current &&
        particles.current.geometry.attributes.position.count
      for (let i = 0; i < l; i++) {
        let vX =
          particles.current.geometry.attributes.position.array[i * 3] /
          (Math.random() * 1000 + 1)
        let vY =
          particles.current.geometry.attributes.position.array[i * 3 + 1] /
          (Math.random() * 400 + 1)
        let vZ =
          particles.current.geometry.attributes.position.array[i * 3 + 2] /
          (Math.random() * 20 + 1)

        let cx = vX * velocity.current * (Math.random() * 10)
        let cy = vY * velocity.current * (Math.random() * 4)
        let cz = vZ * velocity.current * (Math.random() * 1.2)

        if (
          Math.abs(
            particles.current.geometry.attributes.position.array[i * 3]
          ) < 3
        )
          particles.current.geometry.attributes.position.array[i * 3] += cx

        if (
          Math.abs(
            particles.current.geometry.attributes.position.array[i * 3 + 1]
          ) < 5
        )
          particles.current.geometry.attributes.position.array[i * 3 + 1] += cy

        if (
          Math.abs(
            particles.current.geometry.attributes.position.array[i * 3 + 2]
          ) < 6
        )
          particles.current.geometry.attributes.position.array[i * 3 + 2] += cz

        particles.current.geometry.attributes.position.needsUpdate = true
      }
    }
  }

  const polarCoord = () => {
    velocity.current = 1
    particles.current.rotation.y += 0.005

    vector.current.set(pointer.current.x, pointer.current.y, 0.5)
    vector.current.unproject(camera.current)

    let direction = vector.current.sub(camera.current.position).normalize(),
      distance = -camera.current.position.z / direction.z,
      scaled = direction.multiplyScalar(distance),
      mouseXYZ = camera.current.position.clone().add(scaled)

    let l = particles.current.geometry.attributes.position.count

    for (let i = 0; i < l; i++) {
      let x = particles.current.geometry.attributes.position.array[i * 3]
      let y = particles.current.geometry.attributes.position.array[i * 3 + 1]
      let z = particles.current.geometry.attributes.position.array[i * 3 + 2]

      x +=
        Math.cos(angleTo(mouseXYZ, new THREE.Vector3(x, y, z))) *
          (0.04 / mouseXYZ.distanceTo(new THREE.Vector3(x, y, 0))) +
        (origins.current[i * 3] - x) * multiplyOrigin

      y +=
        Math.sin(angleTo(mouseXYZ, new THREE.Vector3(x, y, z))) *
          (0.04 / mouseXYZ.distanceTo(new THREE.Vector3(x, y, 0))) +
        (origins.current[i * 3 + 1] - y) * multiplyOrigin

      z +=
        Math.cos(angleTo(mouseXYZ, new THREE.Vector3(x, y, z))) *
          (0.04 / mouseXYZ.distanceTo(new THREE.Vector3(x, y, 0))) +
        (origins.current[i * 3 + 2] - z) * multiplyOrigin

      particles.current.geometry.attributes.position.array[i * 3] = x
      particles.current.geometry.attributes.position.array[i * 3 + 1] = y
      particles.current.geometry.attributes.position.array[i * 3 + 2] = z
    }

    // helper.current.position.set(mouseXYZ.x, mouseXYZ.y, 0)

    particles.current.geometry.attributes.position.needsUpdate = true
  }

  useEffect(() => {
    setTimeout(() => {
      cancelAnimationFrame(rafDisperse.current)
      cancelAnimationFrame(rafContract.current)
    }, 50)

    setTimeout(() => {
      disperse ? particlesDisperse() : particlesContract()
    }, 60)
    // eslint-disable-next-line
  }, [disperse])

  const particlesContract = _ => {
    // controls.current.autoRotateSpeed = 1
    // controls.current && controls.current.update()
    particles.current && polarCoord()
    rafContract.current = requestAnimationFrame(particlesContract)
    renderer.current && renderer.current.render(scene.current, camera.current)
  }

  const particlesDisperse = _ => {
    // controls.current.autoRotateSpeed = 0.5
    // controls.current && controls.current.update()
    particles.current && pointsRepulse()
    rafDisperse.current = requestAnimationFrame(particlesDisperse)
    renderer.current && renderer.current.render(scene.current, camera.current)
  }

  const inits = vertices => {
    const div = document.getElementById("canvas-wrap")
    renderer.current = new THREE.WebGLRenderer()
    renderer.current.setPixelRatio(window.devicePixelRatio)
    renderer.current.setSize(window.innerWidth, window.innerHeight)
    div.appendChild(renderer.current.domElement)

    scene.current = new THREE.Scene()
    camera.current = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      1,
      10000
    )

    camera.current.position.z = 8

    const light = new THREE.AmbientLight(0x404040, 1)
    scene.current.add(light)

    // controls.current = new OrbitControls(
    //   camera.current,
    //   renderer.current.domElement
    // )
    // controls.current.enableZoom = false
    // controls.current.enablePan = false
    // controls.current.enableRotate = false
    // controls.current.autoRotate = true
    // controls.current.update()

    // helper.current = new THREE.Mesh(
    //   new THREE.SphereGeometry(0.1, 20, 20),
    //   new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    // )
    // scene.current.add(helper.current)

    let positions = new Float32Array(vertices.length * 3)
    let vertex
    for (var i = 0, l = vertices.length; i < l; i++) {
      vertex = vertices[i]
      vertex.toArray(positions, i * 3)
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3))
    const particleMap = new THREE.TextureLoader().load(
      "/textures/home/particle.png"
    )
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.045,
      map: particleMap,
      blending: THREE.AdditiveBlending,
      transparent: true,
    })

    particles.current = new THREE.Points(geometry, material)
    scene.current.add(particles.current)

    window.addEventListener("mousemove", onMouseMove)

    origins.current = particles.current.geometry.attributes.position.array.slice(
      0
    )
  }

  const initCanvas = _ => {
    const objloader = new OBJLoader()
    objloader.load(objiron, model => {
      const geo = model.children[0].geometry
      let geometry = new THREE.Geometry().fromBufferGeometry(geo)
      const modifier = new SubdivisionModifier(2)
      geometry = modifier.modify(geometry)
      geometry.scale(100, 100, 100)
      inits(geometry.vertices)
      rafDisperse.current = window.requestAnimationFrame(particlesDisperse)
    })
  }

  const showInfo = _ => {
    const items = document.querySelectorAll(".item-envolt")
    gsap.to([...items], {
      duration: 0.5,
      display: "block",
      opacity: 1,
      overwrite: true,
      ease: "power3.out",
      stagger: 0.03,
    })
  }

  const hiddenInfo = _ => {
    const items = document.querySelectorAll(".item-envolt")
    gsap.to([...items], {
      duration: 0.5,
      opacity: 0,
      // overwrite: true,
      ease: "power3.out",
      stagger: -0.03,
    })
    gsap.to([...items], {
      delay: 0.56,
      display: "none",
    })
  }

  useEffect(() => {
    initCanvas()
    window.addEventListener("resize", resizeWindow)

    return () => {
      cancelAnimationFrame(rafContract.current)
      cancelAnimationFrame(rafDisperse.current)
      window.removeEventListener("resize", resizeWindow)
      window.removeEventListener("mousemove", onMouseMove)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    scrollActive.current = ScrollTrigger.create({
      trigger: ".iron-points-wrap",
      start: "80% bottom",
      onEnter: () => {
        setDisperse(false)
        showInfo()
      },
      onLeaveBack: () => {
        setDisperse(true)
        hiddenInfo()
      },
    })

    return () => {
      scrollActive.current.kill()
    }
  }, [])

  return (
    <ContactWrapper toggle={toggle}>
      <div className="iron-points-wrap">
        <Locate locateText={locateText} />
        <div id="canvas-wrap" className="canvas-wrap"></div>
      </div>
    </ContactWrapper>
  )
}
